import { useContext, useEffect, useRef, useState } from "react";
import { App, Descriptions, Empty, Modal, Spin } from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import errorCodes from "@/apis/error";
import { fetchRecord } from "@/apis/equipment";
import LogAttachments from "@/components/DetailModal/components/LogHome/LogAttachments";
import { DictContext } from "@/layout/context/DictContext";
import WMSAvatar from "@/styles/WMSAvatar";
import { EquipmentEnumItem, EquipmentRecord } from "@/types/equipment";
import { User } from "@/types/user";
import { getUserAvatarTip } from "@/utils/user";

/** 设备记录详情弹窗入参。 */
interface EquipmentRecordDetailProps {
  open: boolean;
  listRecord: EquipmentRecord; // 列表返回的完整记录，用于补充详情缺失字段
  recordTypeItems: EquipmentEnumItem[];
  onCancel: () => void;
}

/** 将空值统一展示为占位符。 */
const renderValue = (value?: string | number | null) => (
  value === null || value === undefined || value === "" ? "-" : value
);

/** 将时间戳格式化为日期时间。 */
const renderDate = (value?: number | null) => (
  value === null || value === undefined
    ? "-"
    : dayjs(value).format("YYYY-MM-DD HH:mm:ss")
);

// 用户字段只展示头像，姓名和手机号由 WMSAvatar 的悬浮提示提供。
const renderUserAvatar = (user?: User | null) => (
  user ? (
    <WMSAvatar
      creator={user}
      uniChar={user.id}
      avatarId={user.avatar}
      alt={getUserAvatarTip(user)}
      size={24}
    />
  ) : "-"
);

/** 加载并展示设备记录详情。 */
const EquipmentRecordDetail = ({
  open,
  listRecord,
  recordTypeItems,
  onCancel,
}: EquipmentRecordDetailProps) => {
  const { t } = useTranslation();
  const { notification } = App.useApp();
  const { currencys } = useContext(DictContext);
  // 保存当前详情请求，切换记录或关闭弹窗时取消旧请求。
  const controllerRef = useRef<AbortController>();
  const [record, setRecord] = useState<EquipmentRecord>();
  const [loading, setLoading] = useState(false);

  // 弹窗打开后加载详情，并防止旧请求覆盖最新记录。
  useEffect(() => {
    controllerRef.current?.abort();

    if (!open) {
      setRecord(undefined);
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    controllerRef.current = controller;
    setRecord(undefined);
    setLoading(true);
    fetchRecord(listRecord.id, controller)
      .then((detail) => {
        if (controllerRef.current === controller) {
          setRecord(detail);
        }
      })
      .catch((error: unknown) => {
        if (!controller.signal.aborted) {
          notification.error({
            message: t("equipment.recordFetchFailed"),
            description: errorCodes[Number(error)] ?? String(error),
          });
        }
      })
      .finally(() => {
        if (controllerRef.current === controller) {
          setLoading(false);
        }
      });

    return () => controller.abort();
  }, [listRecord.id, notification, open, t]);

  // 将接口枚举值转换为可读的记录类型和币种名称。
  const recordTypeLabel = record
    ? recordTypeItems.find(({ v }) => v === record.recordType)?.desc
      ?? String(record.recordType)
    : "-";
  const currencyLabel = record?.costCurrency === null ||
    record?.costCurrency === undefined
    ? "-"
    : currencys.find(
      ({ key }) => String(key) === String(record.costCurrency),
    )?.desc ?? String(record.costCurrency);

  return (
    <Modal
      title={t("equipment.recordDetail")}
      open={open}
      width={820}
      footer={null}
      destroyOnClose
      styles={{ body: { maxHeight: "65vh", overflowY: "auto" } }}
      onCancel={onCancel}
    >
      {loading ? (
        <div style={{ padding: 48, textAlign: "center" }}>
          <Spin />
        </div>
      ) : record ? (
        <Descriptions bordered size="small" column={2}>
          <Descriptions.Item label={t("equipment.recordType")}>
            {recordTypeLabel}
          </Descriptions.Item>
          <Descriptions.Item label={t("equipment.occurredAt")}>
            {renderDate(record.occurredAt)}
          </Descriptions.Item>
          <Descriptions.Item label={t("equipment.operator")}>
            {renderUserAvatar(listRecord.operatorUser)}
          </Descriptions.Item>
          <Descriptions.Item label={t("equipment.borrower")}>
            {renderUserAvatar(listRecord.borrowerUser)}
          </Descriptions.Item>
          <Descriptions.Item label={t("equipment.expectedReturnAt")}>
            {renderDate(record.expectedReturnAt)}
          </Descriptions.Item>
          <Descriptions.Item label={t("equipment.returnAt")}>
            {renderDate(record.returnAt)}
          </Descriptions.Item>
          <Descriptions.Item label={t("equipment.recordCost")}>
            {renderValue(record.cost)}
          </Descriptions.Item>
          <Descriptions.Item label={t("equipment.recordCostCurrency")}>
            {currencyLabel}
          </Descriptions.Item>
          <Descriptions.Item label={t("equipment.odometer")}>
            {renderValue(record.odometer)}
          </Descriptions.Item>
          <Descriptions.Item label={t("equipment.vendor")}>
            {renderValue(record.vendor)}
          </Descriptions.Item>
          <Descriptions.Item
            label={t("equipment.recordComment")}
            span={2}
          >
            {renderValue(record.comment)}
          </Descriptions.Item>
          <Descriptions.Item label={t("common.attachment")} span={2}>
            {/* 仅在记录关联了文档时加载附件列表。 */}
            {record.docId ? (
              <LogAttachments docId={record.docId} displayMode="list" />
            ) : "-"}
          </Descriptions.Item>
          <Descriptions.Item label={t("equipment.createdBy")}>
            {renderValue(record.createdBy)}
          </Descriptions.Item>
          <Descriptions.Item label={t("equipment.createdAt")}>
            {renderDate(record.createdAt)}
          </Descriptions.Item>
          <Descriptions.Item label={t("equipment.updatedBy")}>
            {renderValue(record.updatedBy)}
          </Descriptions.Item>
          <Descriptions.Item label={t("equipment.updatedAt")}>
            {renderDate(record.updatedAt)}
          </Descriptions.Item>
        </Descriptions>
      ) : (
        <Empty description={t("equipment.noRecordDetail")} />
      )}
    </Modal>
  );
};

export default EquipmentRecordDetail;
